import React, { useContext, useState } from 'react';
import { Pagesection, Tabs, TabsPanel } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Result, CountingCirclesRecord, PollSubType } from 'api/api.types';
import ApiContext from 'api/ApiContext';
import PollBar from 'lib/PollBar/PollBar';
import MunicipalityList from 'lib/MunicipalityList/MunicipalityList';
import PollMap from 'lib/Map/PollMap/PollMap';
import useGetNationalData from './useGetNationalData';

interface Props {
  issueId: string;
  date: string;
  results: Result;
  subtype: PollSubType;
  pollIndex: number;
  isFinal: boolean;
  isNational: boolean;
}

const renderPollAccordion =
  (subtype: PollSubType) => (municipality?: CountingCirclesRecord) => {
    const { results } = municipality ?? {};
    return (
      <PollBar
        result={results?.poll_data}
        participation={results?.participation}
        isReduced
        showVoterInfoAbove
        subtype={subtype}
        eligibleVoters={results?.eligible_voters}
      />
    );
  };

const PollDetails = ({
  issueId,
  date,
  results,
  pollIndex,
  isFinal,
  isNational,
  subtype,
}: Props) => {
  const { t } = useTranslation('wap');
  const [showMap, setShowMap] = useState(true);

  const { getCountingCircles } = useContext(ApiContext);
  const query = useQuery(['counting_circles', issueId], () =>
    getCountingCircles(issueId),
  );

  const { nationalResults, nationalEligibleVoters, nationalParticipation } =
    useGetNationalData(date, pollIndex, isNational, isFinal);

  const hasNationalData = Boolean(nationalResults?.yes_votes);
  const pollDayOverview = results.poll_data;

  const year = new Date(date).getFullYear();

  const isYes = pollDayOverview?.yes_votes > pollDayOverview?.no_votes;
  let mapOutline: string = null;
  if (isFinal) {
    mapOutline = isYes ? 'yes' : 'no';
  }

  return (
    <>
      <Pagesection type="medium">
        <PollBar
          result={pollDayOverview}
          participation={results.participation}
          eligibleVoters={results.eligible_voters}
          subtype={subtype}
        />
        {hasNationalData && isFinal && (
          <>
            <h2 className="poll__progress-label h3">{t('switzerland')}</h2>
            <PollBar
              result={nationalResults}
              participation={nationalParticipation}
              eligibleVoters={nationalEligibleVoters}
              subtype={subtype}
              isReduced
            />
          </>
        )}
      </Pagesection>
      <Pagesection additionalClassNames="poll__switch">
        <h2 className="poll__screenreaderinfo">{t('map_screen_reader_heading')}</h2>
        <Tabs noBezel type="button">
          <TabsPanel
            label={t('map')}
            icon="map"
            onActive={() => setShowMap(true)}
            id="map"
          />
          <TabsPanel
            label={t('municipalities')}
            icon="faders"
            onActive={() => setShowMap(false)}
            id="municipalities"
          />
        </Tabs>
      </Pagesection>
      <Pagesection type="slender">
        {showMap ? (
          <PollMap
            year={year}
            subtype={subtype}
            records={query.data?.data.records}
            mapOutline={mapOutline}
            isNational={isNational}
          />
        ) : (
          <MunicipalityList
            checksum={query.data?.meta?.data_checksum_sha256}
            municipalities={query.data?.data.records}
            render={renderPollAccordion(subtype)}
          />
        )}
      </Pagesection>
    </>
  );
};

export default PollDetails;
